import { useMemo } from 'react';

import { FilterOperator } from '../../../../apis/graphql';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../../constants';
import type { GlobalFilters } from '../../../../contexts/FilterContext';
import { useFiltersContext } from '../../../../contexts/FilterContext';
import { isOperatorFilterValue } from '../../../../contexts/FilterContext/hooks/utils';
import type { SelectedFiltersValues } from '../AdditionalFilters/constants';
import { useGetTraffickingFilterConfig } from '../AdditionalFilters/hooks';

export const useSelectedFiltersValues = (): SelectedFiltersValues => {
  const { filterConfigs } = useGetTraffickingFilterConfig();
  const {
    filters,
    filters: {
      generalPacingStatusList,
      orderTypeList,
      reviewStatusList,
      adStatusList,
      productType,
      adProductList,
      lineItemStatusList,
      billableThirdPartyList,
      priorityValueList,
      tierNameList,
      publisherList,
      adTypeList,
      creativeReviewList,
      creativeTypeList,
      isAddedValue,
      isMakegood,
      isCoppaOrCaru,
      agencyIdList,
      brandIdList,
      excludeBrandIdList,
      brandIdOperator,
      industryIdList,
      currencyList,
      isRotation,
      isSequence,
      countryList,
      doNotTraffic,
      lineItemTypeList,
      percentPacing,
      completionsRemaining,
      lineItemEndDate,
      sspList,
      customPacingApplied,
      costMethodList,
      billableEventList,
      distributionPartnerList,
      CPM,
      frequencyCap,
    },
  } = useFiltersContext();

  const configDrivenSelectedValues = useMemo(() => {
    if (!filterConfigs.length) return {};
    return Object.fromEntries(
      filterConfigs
        .map((config) => [config.id, filters[config.id as keyof GlobalFilters]] as const)
        .filter(([, val]) => val !== null && val !== undefined)
    );
  }, [filterConfigs, filters]);

  // KATANA-1509: Brand's canonical value now lives in filters.brandIdOperator ({operator, values}),
  // with the legacy brandIdList/excludeBrandIdList pair nulled out by FilterPanel once it's set. The
  // chip pipeline below (searchParam swap, Array.isArray/isSimpleDropdownValues checks, chip label,
  // option-label resolution) is built entirely around that legacy pair shape and doesn't need to learn
  // a new one — so derive it back from brandIdOperator here instead. Falls back to the real legacy
  // fields when brandIdOperator isn't set (V1 panel, or a pre-translation load boundary edge case).
  const derivedBrandIdList = isOperatorFilterValue(brandIdOperator)
    ? brandIdOperator.operator === FilterOperator.DoesNotContain
      ? null
      : brandIdOperator.values
    : brandIdList;
  const derivedExcludeBrandIdList = isOperatorFilterValue(brandIdOperator)
    ? brandIdOperator.operator === FilterOperator.DoesNotContain
      ? brandIdOperator.values
      : null
    : excludeBrandIdList;

  return useMemo(
    () => ({
      ...configDrivenSelectedValues,
      [TRAFFICKING_SEARCH_PARAMS.GENERAL_PACING_STATUS_LIST]: generalPacingStatusList,
      [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_TYPE_LIST]: lineItemTypeList,
      [TRAFFICKING_SEARCH_PARAMS.REVIEW_STATUS_LIST]: reviewStatusList,
      [TRAFFICKING_SEARCH_PARAMS.ORDER_TYPE_LIST]: orderTypeList,
      [TRAFFICKING_SEARCH_PARAMS.AD_STATUS_LIST]: adStatusList,
      [TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE]: adProductList ?? productType,
      [TRAFFICKING_SEARCH_PARAMS.CURRENCY_LIST]: currencyList,
      [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST]: lineItemStatusList,
      [TRAFFICKING_SEARCH_PARAMS.BILLABLE_THIRD_PARTY_LIST]: billableThirdPartyList,
      [TRAFFICKING_SEARCH_PARAMS.PRIORITY_VALUE_LIST]: priorityValueList,
      [TRAFFICKING_SEARCH_PARAMS.TIER_NAME_LIST]: tierNameList,
      [TRAFFICKING_SEARCH_PARAMS.PUBLISHER_LIST]: publisherList,
      [TRAFFICKING_SEARCH_PARAMS.AD_TYPE_LIST]: adTypeList,
      [TRAFFICKING_SEARCH_PARAMS.CREATIVE_REVIEW_LIST]: creativeReviewList,
      [TRAFFICKING_SEARCH_PARAMS.CREATIVE_TYPE_LIST]: creativeTypeList,
      [TRAFFICKING_SEARCH_PARAMS.IS_ADDED_VALUE]: isAddedValue,
      [TRAFFICKING_SEARCH_PARAMS.IS_MAKEGOOD]: isMakegood,
      [TRAFFICKING_SEARCH_PARAMS.IS_COPPA_OR_CARRU]: isCoppaOrCaru,
      [TRAFFICKING_SEARCH_PARAMS.AGENCY_ID_LIST]: agencyIdList,
      [TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST]: derivedBrandIdList,
      [TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST]: derivedExcludeBrandIdList,
      [TRAFFICKING_SEARCH_PARAMS.INDUSTRY_ID_LIST]: industryIdList,
      [TRAFFICKING_SEARCH_PARAMS.IS_ROTATION]: isRotation,
      [TRAFFICKING_SEARCH_PARAMS.IS_SEQUENCE]: isSequence,
      [TRAFFICKING_SEARCH_PARAMS.COUNTRY_LIST]: countryList,
      [TRAFFICKING_SEARCH_PARAMS.DO_NOT_TRAFFIC]: doNotTraffic,
      [TRAFFICKING_SEARCH_PARAMS.PERCENT_PACING]: percentPacing,
      [TRAFFICKING_SEARCH_PARAMS.COMPLETIONS_REMAINING]: completionsRemaining,
      [TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_END_DATE]: lineItemEndDate,
      [TRAFFICKING_SEARCH_PARAMS.SSP_LIST]: sspList,
      [TRAFFICKING_SEARCH_PARAMS.CUSTOM_PACING_APPLIED]: customPacingApplied,
      [TRAFFICKING_SEARCH_PARAMS.COST_METHOD_LIST]: costMethodList,
      [TRAFFICKING_SEARCH_PARAMS.BILLABLE_EVENT_LIST]: billableEventList,
      [TRAFFICKING_SEARCH_PARAMS.DISTRIBUTION_PARTNER_LIST]: distributionPartnerList,
      [TRAFFICKING_SEARCH_PARAMS.CPM]: CPM,
      [TRAFFICKING_SEARCH_PARAMS.FREQUENCY_CAP]: frequencyCap,
    }),
    [
      configDrivenSelectedValues,
      generalPacingStatusList,
      lineItemTypeList,
      reviewStatusList,
      orderTypeList,
      adStatusList,
      productType,
      adProductList,
      currencyList,
      lineItemStatusList,
      billableThirdPartyList,
      priorityValueList,
      tierNameList,
      publisherList,
      adTypeList,
      creativeReviewList,
      creativeTypeList,
      isAddedValue,
      isMakegood,
      isCoppaOrCaru,
      agencyIdList,
      derivedBrandIdList,
      derivedExcludeBrandIdList,
      industryIdList,
      isRotation,
      isSequence,
      countryList,
      doNotTraffic,
      percentPacing,
      completionsRemaining,
      lineItemEndDate,
      sspList,
      customPacingApplied,
      costMethodList,
      billableEventList,
      distributionPartnerList,
      CPM,
      frequencyCap,
    ]
  );
};
